import axios from 'axios';
import { getAuthToken } from '../utils/sessionAuth.js';

const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/api/software-updates`;

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

export function parseSoftwareUpdateIdentifier(identifier) {
  const match = String(identifier || '').match(/^xavier\.su\.(ios|macos|visionos)\.(.+)$/);
  if (!match) return { family: null, productVersion: null };
  return { family: match[1], productVersion: match[2] };
}

export function softwareUpdateFamilyLabel(family) {
  if (family === 'macos') return 'macOS';
  if (family === 'visionos') return 'visionOS';
  return 'iOS / iPadOS / tvOS';
}

export function softwareUpdateVersionLabel(identifier) {
  if (!identifier || identifier === 'none') return 'kein Update';
  const { family, productVersion } = parseSoftwareUpdateIdentifier(identifier);
  if (!productVersion) return identifier;
  return `${productVersion} · ${softwareUpdateFamilyLabel(family)}`;
}

export function defaultDeadlineLocal() {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  date.setHours(18, 0, 0, 0);
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function toTargetLocalDateTime(value) {
  if (!value) return undefined;
  return value.length === 16 ? `${value}:00` : value;
}

const getCatalog = async ({ family, model, refresh } = {}) => {
  const params = {};
  if (family) params.family = family;
  if (model) params.model = model;
  if (refresh) params.refresh = '1';
  const response = await axios.get(`${API_URL}/catalog`, { headers: authHeaders(), params, timeout: 30000 });
  return response.data;
};

const getCatalogs = async ({ refresh } = {}) => {
  const [ios, macos] = await Promise.all([
    getCatalog({ family: 'ios', refresh }),
    getCatalog({ family: 'macos', refresh }),
  ]);
  return {
    ios: ios.catalogs?.ios || ios.catalog || [],
    macos: macos.catalogs?.macos || macos.catalog || [],
  };
};

const getDeviceSoftwareUpdate = async (udid, serialNumber) => {
  const response = await axios.get(`${API_URL}/device/${encodeURIComponent(udid)}`, {
    headers: authHeaders(),
    params: serialNumber ? { serialNumber } : {},
  });
  return response.data;
};

const setDeviceSoftwareUpdate = async (udid, payload) => {
  const response = await axios.put(
    `${API_URL}/device/${encodeURIComponent(udid)}`,
    payload,
    { headers: jsonHeaders() }
  );
  return response.data;
};

const setGroupSoftwareUpdate = async (groupId, payload) => {
  const response = await axios.put(
    `${API_URL}/groups/${encodeURIComponent(groupId)}`,
    payload,
    { headers: jsonHeaders() }
  );
  return response.data;
};

const lookupPolicies = async (udids) => {
  const response = await axios.post(
    `${API_URL}/policies`,
    { udids },
    { headers: jsonHeaders() }
  );
  return response.data;
};

const softwareUpdateService = {
  getCatalog,
  getCatalogs,
  getDeviceSoftwareUpdate,
  setDeviceSoftwareUpdate,
  setGroupSoftwareUpdate,
  lookupPolicies,
};
export default softwareUpdateService;
